import { useState } from 'react'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { useCart } from '@/providers/CartProvider'
import { cartApi } from '@/api/cart'
import { useProducts } from '@/hooks/useProducts'
import { useBranding } from '@/branding/BrandingProvider'
import type { Product } from '@/types'

export function CheckoutPage() {
  const { items } = useCart()
  const { products } = useProducts()
  const { siteName } = useBranding()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const productMap = new Map(products.map((p) => [p.id, p]))
  const cartProducts = items
    .map((item) => {
      const product = productMap.get(item.productId)
      return product ? { ...item, product } : null
    })
    .filter(Boolean) as { productId: string; quantity: number; product: Product }[]

  const subtotal = cartProducts.reduce((sum, item) => sum + item.product.price * item.quantity, 0)

  const handleCheckout = async () => {
    setLoading(true)
    setError('')
    try {
      const { sessionUrl } = await cartApi.checkout()
      if (!sessionUrl) throw new Error('Checkout session URL missing')
      // Redirect to Stripe-hosted checkout; on success/cancel the storefront
      // handles navigation via success_url / cancel_url on the session.
      window.location.href = sessionUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed')
      setLoading(false)
    }
  }

  if (cartProducts.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Your cart is empty.</p>
          <Link to="/" className="text-rust hover:text-navy transition-colors text-sm font-medium">
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-100 h-16 flex items-center px-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-navy flex items-center justify-center">
            <Icon icon="solar:health-linear" className="text-white text-sm" />
          </div>
          <span className="text-lg font-medium text-navy tracking-tight">{siteName}</span>
        </Link>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/cart" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-8 transition-colors">
          <Icon icon="solar:arrow-left-linear" /> Back to Cart
        </Link>

        <h1 className="text-2xl font-medium tracking-tight text-navy mb-8">Order Summary</h1>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          {cartProducts.map((item) => (
            <div key={item.productId} className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
              <div>
                <span className="font-medium text-navy">{item.product.name}</span>
                <span className="text-sm text-slate-400 ml-2">x{item.quantity}</span>
              </div>
              <span className="font-medium text-navy">${item.product.price * item.quantity}</span>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <span className="text-lg font-medium text-navy">Total</span>
            <span className="text-2xl font-medium text-rust">${subtotal}</span>
          </div>

          <button
            onClick={handleCheckout}
            disabled={loading}
            className="w-full bg-rust text-white py-3 rounded-full font-medium text-sm hover:bg-rust-dark transition-colors shadow-lg shadow-rust/20 disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Place Order'}
          </button>
        </div>
      </div>
    </div>
  )
}
